import { AutocompleteInput } from "@/components/ui/AutocompleteInput";
import { Select } from "@/components/ui/Select";
import React from "react";

import { VisitCitationRowsEditor } from "./VisitCitationRowsEditor";
import type {
  CaseFileOption,
  CitationRowErrors,
  MedChronTemplate,
  VisitFieldSuggestions,
} from "./types";
import type { BulkEditHeaderValues } from "./types/bulkEdit";
import { buildFileOptions, buildVisitTypeOptions } from "./utils/visitFormatting";

export interface VisitsBulkEditFormViewProps {
  values: BulkEditHeaderValues;
  onChange: React.Dispatch<React.SetStateAction<BulkEditHeaderValues>>;
  /** Index-aligned with values.citations; empty while errors are hidden. */
  citationRowErrors: CitationRowErrors[];
  template: MedChronTemplate | null;
  availableFiles: CaseFileOption[];
  fileMapping: Record<string, string>;
  fieldSuggestions: VisitFieldSuggestions;
  isSaving: boolean;
}

const labelClassName = "mb-1 block text-xs font-semibold text-neutral-600";
const fieldClassName =
  "w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus-visible:shadow-focus";

export const VisitsBulkEditFormView: React.FC<VisitsBulkEditFormViewProps> = ({
  values,
  onChange,
  citationRowErrors,
  template,
  availableFiles,
  fileMapping,
  fieldSuggestions,
  isSaving,
}) => (
  <div className="space-y-4 px-6 py-5">
    <p className="text-sm text-neutral-500">
      Only fill in fields you want to change (visit type, provider, facility, subtype, citations).
      Empty fields are left unchanged on each selected visit. Visit dates are not bulk-editable.
      Citations replace each updated visit&apos;s whole citation list.
    </p>

    <div>
      <label htmlFor="bulk-edit-visit-type" className={labelClassName}>
        Visit type
      </label>
      <Select
        id="bulk-edit-visit-type"
        value={values.visit_type_id}
        onChange={(next) =>
          onChange((prev) => ({
            ...prev,
            visit_type_id: next,
          }))
        }
        options={buildVisitTypeOptions(template)}
        placeholder="Select visit type"
        disabled={isSaving}
        className={fieldClassName}
      />
    </div>

    <div>
      <label htmlFor="bulk-edit-subtype" className={labelClassName}>
        Subtype
      </label>
      <AutocompleteInput
        id="bulk-edit-subtype"
        value={values.visit_subtype}
        suggestions={fieldSuggestions.subtypes}
        onChange={(next) => onChange((prev) => ({ ...prev, visit_subtype: next }))}
        disabled={isSaving}
        placeholder="Enter subtype"
        className={fieldClassName}
      />
    </div>

    <div>
      <label htmlFor="bulk-edit-provider" className={labelClassName}>
        Provider
      </label>
      <AutocompleteInput
        id="bulk-edit-provider"
        value={values.provider_name}
        suggestions={fieldSuggestions.providers}
        onChange={(next) => onChange((prev) => ({ ...prev, provider_name: next }))}
        disabled={isSaving}
        placeholder="Enter provider name"
        className={fieldClassName}
      />
    </div>

    <div>
      <label htmlFor="bulk-edit-facility" className={labelClassName}>
        Facility
      </label>
      <AutocompleteInput
        id="bulk-edit-facility"
        value={values.facility_name}
        suggestions={fieldSuggestions.facilities}
        onChange={(next) => onChange((prev) => ({ ...prev, facility_name: next }))}
        disabled={isSaving}
        placeholder="Enter facility name"
        className={fieldClassName}
      />
    </div>

    <fieldset>
      <legend className={labelClassName}>Citations</legend>
      <VisitCitationRowsEditor
        rows={values.citations}
        rowErrors={citationRowErrors}
        fileOptions={buildFileOptions(availableFiles, fileMapping)}
        onChange={(citations) => onChange((prev) => ({ ...prev, citations }))}
      />
    </fieldset>
  </div>
);
